import React from 'react';
import DataGrid from '@dataGrid';
import type { ColDef, MasterDetailContext } from '@dataGrid/MasterDetails';
import { withTheme } from '@material-ui/core';
import { compose } from 'recompose';
import { inject } from 'mobx-react';
import { withUserProfile } from '../../../../../../userProfile/userProfileContext';
import { doesTrailerMovePassFilters, getGroupRowStyle } from '../filtering/filterUtils';
import { DETAIL_GRID_THEME_FALLBACK } from '../constants';
import { useDetailGridSelectionSync } from '../hooks/useDetailGridSelectionSync';
import type {
  SwapBoardDispatchStore,
  SwapBoardTrailer,
  TrailerMoveMasterRow,
  TrailerMoveFilterOption,
  GoldenLayoutContainer,
} from '../types';
import type { AppTheme } from '../styles';
import {
  buildDetailGridConfig,
  findLocalPickupIdMatch,
  getDetailGridBorderStyle,
  getDetailGridSizing,
  getDetailPanelLayoutStyle,
  getTrailersForShipperGroup,
} from '../utils';
import './TrailerMovesDetailGrid.scss';

export interface TrailerMovesDetailGridProps {
  data?: TrailerMoveMasterRow;
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  headerFilter?: TrailerMoveFilterOption;
  panelFilter?: string;
  searchText?: string;
  terminalOffset?: string;
  clearSelectionCount: number;
  selectedDetailPickupId?: string | null;
  onDetailSelectionChanged?: (selectedRow: SwapBoardTrailer | null) => void;
  layoutContainer?: GoldenLayoutContainer;
  theme?: AppTheme;
  userData?: { userProfile?: { theme?: string } };
  DispatchStore?: SwapBoardDispatchStore;
  context?: MasterDetailContext;
}

/** Plain function component — mobx-react@5 observer wraps as a class and breaks hooks. */
export function TrailerMovesDetailGrid({
  data,
  columnDefs,
  defaultColDef,
  headerFilter,
  panelFilter,
  searchText,
  terminalOffset,
  clearSelectionCount,
  selectedDetailPickupId,
  onDetailSelectionChanged,
  layoutContainer,
  theme,
  userData,
  DispatchStore,
  context,
}: TrailerMovesDetailGridProps) {
  const leftOffset = context?.detailPanelLeftOffset ?? 0;
  const shipperCode = data?._parentId ?? data?.rmaxShipperCode;
  const themeName = userData?.userProfile?.theme ?? theme?.themeName ?? DETAIL_GRID_THEME_FALLBACK;
  const detailGridConfig = buildDetailGridConfig({ defaultColDef, columnDefs });

  const liveTrailers = getTrailersForShipperGroup({
    swapTrailers: DispatchStore?.swapTrailers,
    shipperCode,
    fallbackTrailers: data?.trailers ?? [],
  });

  const filteredTrailers = liveTrailers.filter((trailer) =>
    doesTrailerMovePassFilters({ row: trailer, headerFilter, panelFilter, searchText, terminalOffset })
  );

  const rowCount = filteredTrailers.length;
  const activePickupId = findLocalPickupIdMatch(
    selectedDetailPickupId,
    filteredTrailers.map((trailer) => trailer.pickupId)
  );
  const { gridHeight, panelHeight, useAutoHeight } = getDetailGridSizing(rowCount);
  const { onGridReady, onSelectionChanged } = useDetailGridSelectionSync({
    activePickupId,
    clearSelectionCount,
    onDetailSelectionChanged,
  });
  const popupParent = layoutContainer?.getElement?.()?.[0] ?? document.body;

  if (!rowCount) return null;

  const panelStyle = {
    ...getGroupRowStyle(theme),
    ...getDetailPanelLayoutStyle({
      leftOffset,
      panelHeight,
      borderColor: theme?.gridGroupHeaderBorderColor,
    }),
  };

  const gridWrapperStyle = {
    ...getDetailGridBorderStyle(theme),
    height: useAutoHeight ? 'auto' : `${gridHeight}px`,
    width: '100%',
  };

  return (
    <div
      className={`trailerMovesDetailGrid gridTheme-${themeName}${
        useAutoHeight ? ' trailerMovesDetailGrid--autoHeight' : ''
      }`}
      style={panelStyle}
    >
      <div style={gridWrapperStyle}>
        <DataGrid
          config={detailGridConfig}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowSelection={detailGridConfig.rowSelection}
          rowDeselection={detailGridConfig.rowDeselection}
          rowData={filteredTrailers}
          suppressMovableColumns
          domLayout={useAutoHeight ? 'autoHeight' : undefined}
          getRowId={(params: { data: SwapBoardTrailer }) => params.data.pickupId}
          popupParent={popupParent}
          postProcessPopup={(params: { ePopup?: HTMLElement }) => {
            if (params?.ePopup) params.ePopup.style.zIndex = '100';
          }}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </div>
  );
}

export default compose(
  withUserProfile,
  withTheme(),
  inject('DispatchStore')
)(TrailerMovesDetailGrid) as React.ComponentType<TrailerMovesDetailGridProps>;
